import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../store/auth";
import { useToast } from "../store/toast";
import Icon from "./Icon";

// Puente Tareas → Compras. Una tarea de Insumos (solicitud) o Equipos
// (inventario) ya tiene proyecto, departamento, quién la pidió y la lista de
// ítems: convertirla en requisición es copiar eso, no volver a teclearlo.
//
// La tarea NO se cierra al convertir: queda enlazada y sigue viva, así quien la
// pidió ve por dónde va la compra sin salir de su tarea.

const ESTADO_LABEL = {
  borrador: ["En elaboración", "bg-slate-100 text-slate-600"],
  pendiente_supervisor: ["Esperando al supervisor", "bg-amber-100 text-amber-700"],
  pendiente_evaluacion: ["Cotizando", "bg-violet-100 text-violet-700"],
  pendiente_aprobacion_compra: ["En aprobación de compra", "bg-amber-100 text-amber-800"],
  aprobada: ["Aprobada", "bg-emerald-100 text-emerald-700"],
  convertida_en_oc: ["Orden de compra emitida", "bg-brand-100 text-brand-700"],
  rechazada: ["Rechazada", "bg-rose-100 text-rose-700"],
  cancelada: ["Cancelada", "bg-slate-200 text-slate-500"],
};

export default function ConvertirEnRequisicion({ taskId, onDone }) {
  const nav = useNavigate();
  const { can, features } = useAuth();
  const { push } = useToast();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!can("compras") || !features?.compras) return;
    api.get(`/compras/requisiciones/de-tarea/${taskId}`)
      .then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [taskId, can, features]);
  useEffect(load, [load]);

  const convertir = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/compras/requisiciones/desde-tarea/${taskId}`, {});
      push(`Requisición ${data.numero} creada con ${data.lineas?.length || 0} líneas`, "success");
      onDone?.();
      // Lleva directo a la requisición: el siguiente paso es anexar los
      // presupuestos de los proveedores y enviarla a aprobación.
      nav(`/compras/requisiciones/${data.id}`);
    } catch (e) {
      push(e.response?.data?.detail || "No se pudo convertir", "error");
    } finally { setBusy(false); }
  };

  if (!can("compras") || !features?.compras || !info?.convertible) return null;

  if (info.requisicion) {
    const r = info.requisicion;
    const [label, cls] = ESTADO_LABEL[r.estado] || [r.estado, "bg-slate-100 text-slate-600"];
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2">
        <Icon name="receipt" size={15} className="text-amber-600" />
        <span className="text-sm text-slate-600">Esta solicitud ya es la requisición</span>
        <button className="text-sm font-bold text-brand-700 hover:underline"
          onClick={() => nav(`/compras/requisiciones/${r.id}`)}>{r.numero}</button>
        <span className={`rounded-tag px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>
        {r.total_cents > 0 && (
          <span className="ml-auto text-sm font-bold tabular-nums text-slate-700">
            {(r.total_cents / 100).toLocaleString("es-PA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.moneda}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
      <Icon name="receipt" size={15} className="text-slate-400" />
      <span className="text-sm text-slate-600">
        {info.items > 0
          ? <>Sus {info.items} ítem{info.items === 1 ? "" : "s"} pueden pasar a una requisición de compra.</>
          : <>Añade ítems a la tarea para poder convertirla en requisición.</>}
      </span>
      <button className="btn-primary ml-auto text-xs" disabled={busy || !info.items} onClick={convertir}>
        <Icon name="arrow-right" size={13} /> Convertir en requisición
      </button>
    </div>
  );
}
